// src/padplanner/MapHud.jsx
//
// Everything that floats on a PadPlanner map, in one layout. Before this,
// each slice placed its own overlay with absolute offsets (bottom-16,
// bottom-20, top-14, right-[150px]...), and they ran into each other: the
// Layout checks panel over the versions strip, the strip off the map's right
// edge, two notices on the same bottom-center spot, a failure note on the
// zoom control (S6a2 browser test, 2026-10-29).
//
// ── SIX SLOTS, ONE GRID ────────────────────────────────────────────────────
//
//   topLeft     topCenter     topRight
//   (the map's middle: nothing floats here)
//   bottomLeft  bottomCenter  bottomRight
//
// The slots are the cells of one CSS grid over the map, so two slots cannot
// overlap: a cell is never another cell. Within a slot, items stack with a
// fixed gap (HUD_GAP_PX), top-down at the top and bottom-up at the bottom.
//
//   columns   minmax(0,1fr) fit-content(60%) minmax(0,1fr): the center is
//             centered and at most 60% wide; the sides share the rest
//             equally. A side item narrower than its content wraps or
//             scrolls (max-w-full); it never reaches the center.
//   rows      minmax(0,auto) minmax(0,1fr) minmax(0,auto): the top and
//             bottom rows are their content's height until the map is too
//             short for both, then they share it and a bottom slot scrolls.
//             The middle row takes what is left.
//
// TOP SLOTS DO NOT CLIP. Their menus (the basemap picker, the editor's Snap
// and Layers menus) open downward into the map's empty middle, so a top slot
// is overflow: visible. A bottom slot scrolls (overflow: auto).
//
// MapLibre's own controls are moved into the slots by PadPlannerMap: the zoom
// control is the first item top-left. The scale bar and the credits are in
// the credits footer along the bottom edge (components/map/MapCreditsFooter.jsx),
// not a slot: insetBottom, the footer's height, keeps the bottom row above it,
// so the strip sits directly on it and nothing is drawn over a credit.
//
// Above the map's DOM label overlays (z-index 3): a plan-number label or a
// comment pin passes under the chrome, never over it.
import { Children, isValidElement } from "react";
import "./mapHud.css";

export const HUD_SLOTS = Object.freeze(["topLeft", "topCenter", "topRight", "bottomLeft", "bottomCenter", "bottomRight"]);
export const HUD_GAP_PX = 8;

const PLACE = Object.freeze({
  topLeft: "col-start-1 row-start-1 items-start justify-start overflow-visible",
  topCenter: "col-start-2 row-start-1 items-center justify-start overflow-visible",
  topRight: "col-start-3 row-start-1 items-end justify-start overflow-visible",
  bottomLeft: "col-start-1 row-start-3 items-start justify-end overflow-auto",
  bottomCenter: "col-start-2 row-start-3 items-center justify-end overflow-auto",
  bottomRight: "col-start-3 row-start-3 items-end justify-end overflow-auto",
});

/** The bottom row's distance above the credits footer: the strip sits directly on it. */
export const HUD_FOOTER_GAP_PX = 4;

/**
 * slots: {topLeft, ...}: each a node or an array of nodes; falsy items are left out.
 * insetBottom: the credits footer's height (px); the grid ends HUD_FOOTER_GAP_PX above it.
 */
export default function MapHud({ slots = {}, insetBottom = 0 }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-[3] grid p-2"
      style={{
        paddingBottom: insetBottom > 0 ? `${insetBottom + HUD_FOOTER_GAP_PX}px` : undefined,
        gap: `${HUD_GAP_PX}px`,
        gridTemplateColumns: "minmax(0, 1fr) fit-content(60%) minmax(0, 1fr)",
        gridTemplateRows: "minmax(0, auto) minmax(0, 1fr) minmax(0, auto)",
      }}
      data-testid="pp-map-hud">
      {HUD_SLOTS.map((name) => <Slot key={name} name={name} items={slots[name]} />)}
    </div>
  );
}

function Slot({ name, items }) {
  const shown = Children.toArray(items).filter((c) => c !== null && c !== false && c !== undefined && c !== "");
  return (
    <div className={`pointer-events-none flex min-h-0 min-w-0 flex-col ${PLACE[name]}`} style={{ gap: `${HUD_GAP_PX}px` }}
      data-hud-slot={name}>
      {shown.map((c, i) => (
        // Only an item takes the pointer: the slot's empty space is the map's.
        <div key={isValidElement(c) && c.key !== null ? c.key : i} className="pointer-events-auto min-w-0 max-w-full" data-hud-item>
          {c}
        </div>
      ))}
    </div>
  );
}
